import React from 'react';
import { ColumnConfig } from 'components/table';
import CURRENCY from 'utils/currency';
import { formatToTz } from 'utils/date-format';
import STRING from 'utils/string';
import { GTW_TRANSACTION_TYPE } from 'utils/constants';
import {
  gatewatTransactionStatusFormatter,
  transactionStatusFormatter,
} from 'utils/status-formatter';
import { TransactionTableData, TransactionColumnsParams } from '../types';
import { TransactionActionsMenu } from './TransactionActionsMenu';

/**
 * Factory function to create column configurations for the transaction table
 */
export function createTransactionColumns({
  settings,
  isOrgLevel,
  getStoreName,
  isGatewayTransactionID,
  onVerifySale,
  onRecordSale,
  onRefund,
  canRecordSale,
}: TransactionColumnsParams): ColumnConfig<TransactionTableData>[] {
  const columns: ColumnConfig<TransactionTableData>[] = [
    {
      key: 'transactionDate',
      label: 'Date',
      sortable: true,
      isDate: true,
      renderCell: (row) => (
        <>
          {row.transactionDate
            ? formatToTz(row.transactionDate, settings?.timezone, 'MMM dd, hh:mm a')
            : ''}
        </>
      ),
    },
  ];

  // Add store column for org-level view
  if (isOrgLevel) {
    columns.push({
      key: 'store_name' as keyof TransactionTableData,
      label: 'Store',
      sortable: true,
      renderCell: (row) => <div className="capitalize">{getStoreName(row)}</div>,
    });
  }

  columns.push(
    {
      key: 'grossAmount',
      label: 'Gross Amount',
      sortable: true,
      renderCell: (row) => (
        <div className="text-gray-blue-60 font-medium">
          {CURRENCY.formatSignedAmount(row.type, row.grossAmount)}
        </div>
      ),
    },
    {
      key: 'order_fee_amount',
      label: 'Convenience Fee',
      sortable: true,
      renderCell: (row) => (
        <div className="text-gray-blue-60 font-medium">
          {CURRENCY.formatSignedAmount(row.type, row.order_fee_amount)}
        </div>
      ),
    },
    {
      key: 'transaction_status',
      label: 'Status',
      renderCell: (row) => {
        let status = row?.transaction_status || row?.order_status;

        if (isGatewayTransactionID(row.id)) {
          if (row?.adjusted_status !== null) {
            const adjustedStatus = gatewatTransactionStatusFormatter(row?.adjusted_status);

            return (
              <span>
                {adjustedStatus}
                <p className="text-xs text-gray-blue-60 font-normal leading-4">
                  Sale recorded as successful by {row.treez_employee_first_name}
                </p>
              </span>
            );
          }
          return gatewatTransactionStatusFormatter(
            row.type === GTW_TRANSACTION_TYPE.REFUND ? row.type : row?.gateway_status
          );
        }

        if (
          status === 'inited' &&
          (String(row?.ach_debit_error_count) === '1' ||
            String(row?.ach_debit_error_count) === '2')
        ) {
          status = `${status} Retry ${String(row?.ach_debit_error_count)}`;
        }
        return transactionStatusFormatter(status);
      },
    },
    {
      key: 'method',
      label: 'Payment Type',
      renderCell: (row) => (
        <>{!row.method ? 'ACH' : row.method === 'ATM' ? 'CATM' : row.method}</>
      ),
    },
    {
      key: 'track_id',
      label: 'Ticket id',
      renderCell: (row) => {
        const referenceNumberParts =
          STRING.getTreezTicketIDFromReferenceNumber(row.reference_number) || 'NA';
        return referenceNumberParts ? <>{referenceNumberParts}</> : <>{row.track_id}</>;
      },
    },
    {
      key: 'customerName',
      label: 'Customer',
      sortable: true,
      renderCell: (row) => <div className="capitalize">{row.customerName || 'NA'}</div>,
    },
    {
      key: 'transaction_action',
      label: '',
      classNameCell: 'overflow-visible',
      renderCell: (row) => (
        <TransactionActionsMenu
          row={row}
          onVerifySale={onVerifySale}
          onRecordSale={onRecordSale}
          onRefund={onRefund}
          canRecordSale={canRecordSale}
        />
      ),
    }
  );

  return columns;
}
